<template>
  <svg
    class="module-shape-mask"
    :width="moduleWidth + 40"
    :height="moduleHeight + 40"
    pointer-events="none"
  >
    <!-- This svg is the size of the module, plus 20px on each side to account for a border -->
    <defs>
      <!--
        This pattern is used for the border fill. If the border type is "clouds"
        it will include the clouds overlay; otherwise it's just a solid fill color
      -->
      <pattern
        :id="`pattern-fill-${moduleId}`"
        patternUnits="userSpaceOnUse"
        width="1367"
        height="650"
      >
        <rect
          :class="themeClass"
          width="1367"
          height="650"
        />
        <image
          v-if="borderType === 'clouds'"
          href="@/assets/border-overlay-texture.png"
          x="0"
          y="0"
          width="1367"
          height="650"
        />
      </pattern>

      <!-- This clipPath is used to mask the module contents into a shape -->
      <clipPath
        v-if="!backgroundMaskColor"
        :id="`module-shape-${moduleId}`"
      >
        <circle
          v-if="shape === 'circle'"
          :cx="moduleWidth / 2"
          :cy="moduleHeight / 2"
          :r="Math.min(moduleHeight, moduleWidth) / 2"
        />
        <path
          v-else-if="shape === 'pentagon'"
          :d="pentagonPath"
        />
        <path
          v-else-if="shape === 'rounded_rectangle'"
          :d="roundedRectanglePath"
        />
        <path
          v-else-if="shape === 'speech_bubble_lower_left'"
          :d="speechBubbleLowerLeftPath"
        />
        <!-- Speech Bubble Right is a mirrored version of Speech Bubble Left -->
        <path
          v-else-if="shape === 'speech_bubble_lower_right'"
          :d="speechBubbleLowerLeftPath"
          :transform="`translate(${moduleWidth}, 0), scale(-1, 1)`"
        />
        <path
          v-else-if="shape === 'title_bubble'"
          :d="titleBubblePath"
        />
      </clipPath>
    </defs>

    <template v-if="backgroundMaskColor">
      <mask :id="`shape-mask-${moduleId}`">
        <rect
          x="20"
          y="20"
          rx="6"
          :width="moduleWidth"
          :height="moduleHeight"
          :fill="backgroundMaskColor"
        />
        <circle
          v-if="shape === 'circle'"
          :cx="(moduleWidth + 40) / 2"
          :cy="(moduleHeight + 40) / 2"
          :r="Math.min(moduleHeight, moduleWidth) / 2"
          fill="black"
        />
        <path
          v-else-if="shape === 'pentagon'"
          :d="pentagonPath"
          fill="black"
        />
        <path
          v-else-if="shape === 'rounded_rectangle'"
          :d="roundedRectanglePath"
          fill="black"
        />
        <path
          v-else-if="shape === 'speech_bubble_lower_left'"
          :d="speechBubbleLowerLeftPath"
          fill="black"
        />
        <!-- Speech Bubble Right is a mirrored version of Speech Bubble Left -->
        <path
          v-else-if="shape === 'speech_bubble_lower_right'"
          :d="speechBubbleLowerLeftPath"
          :transform="`translate(${moduleWidth}, 0), scale(-1, 1)`"
          fill="black"
        />
        <path
          v-else-if="shape === 'title_bubble'"
          :d="titleBubblePath"
          fill="black"
        />
      </mask>

      <rect
        x="0"
        y="0"
        rx="6"
        :fill="nebulaColorWhite"
        :width="moduleWidth + 40"
        :height="moduleHeight + 40"
        :mask="`url(#shape-mask-${moduleId})`"
      />
    </template>

    <!-- This mask and circle is for a circle border around the module -->
    <template v-if="shape === 'circle' && borderType">
      <mask :id="`module-shape-circle-mask-${moduleId}`">
        <circle
          :cx="(moduleWidth / 2) + 20"
          :cy="(moduleHeight / 2) + 20"
          :r="(Math.min(moduleHeight, moduleWidth) / 2) + 20"
          fill="white"
        />
        <circle
          :cx="(moduleWidth / 2) + 20"
          :cy="(moduleHeight / 2) + 20"
          :r="Math.min(moduleHeight, moduleWidth) / 2"
          fill="black"
        />
      </mask>
      <circle
        :cx="(moduleWidth / 2) + 20"
        :cy="(moduleHeight / 2) + 20"
        :r="(Math.min(moduleHeight, moduleWidth) / 2) + 20"
        :mask="`url(#module-shape-circle-mask-${moduleId})`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>

    <!-- This mask and path is for a border around Pentagon -->
    <template v-else-if="shape === 'pentagon' && borderType">
      <mask :id="`module-shape-pentagon-mask-${moduleId}`">
        <path
          :d="pentagonPathOutline"
          fill="white"
        />
        <path
          :d="pentagonPath"
          fill="black"
          :transform="backgroundMaskColor ? undefined : 'translate(20, 20)'"
        />
      </mask>
      <path
        :d="pentagonPathOutline"
        :mask="`url(#module-shape-pentagon-mask-${moduleId})`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>

    <!-- This mask and path is for a border around Rounded Rectangle -->
    <template v-else-if="shape === 'rounded_rectangle' && borderType">
      <mask :id="`module-shape-rounded-rectangle-mask-${moduleId}`">
        <path
          :d="roundedRectanglePathOutline"
          fill="white"
        />
        <path
          :d="roundedRectanglePath"
          fill="black"
          :transform="backgroundMaskColor ? undefined : 'translate(20, 20)'"
        />
      </mask>
      <path
        :d="roundedRectanglePathOutline"
        :mask="`url(#module-shape-rounded-rectangle-mask-${moduleId})`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>

    <!-- This mask and path is for a border around Speech Bubble Left -->
    <template v-else-if="shape === 'speech_bubble_lower_left' && borderType">
      <mask :id="`module-shape-speech-bubble-lower-left-mask-${moduleId}`">
        <path
          :d="speechBubbleLowerLeftPathOutline"
          fill="white"
        />
        <path
          :d="speechBubbleLowerLeftPath"
          fill="black"
          :transform="backgroundMaskColor ? undefined : 'translate(20, 20)'"
        />
      </mask>
      <path
        :d="speechBubbleLowerLeftPathOutline"
        :mask="`url(#module-shape-speech-bubble-lower-left-mask-${moduleId})`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>

    <!-- This mask and path is for a border around Speech Bubble Right -->
    <template v-else-if="shape === 'speech_bubble_lower_right' && borderType">
      <mask :id="`module-shape-speech-bubble-lower-right-mask-${moduleId}`">
        <path
          :d="speechBubbleLowerLeftPathOutline"
          fill="white"
        />
        <path
          :d="speechBubbleLowerLeftPath"
          fill="black"
          :transform="backgroundMaskColor ? 'translate(40, 0)' : 'translate(20, 20)'"
        />
      </mask>
      <path
        :d="speechBubbleLowerLeftPathOutline"
        :mask="`url(#module-shape-speech-bubble-lower-right-mask-${moduleId})`"
        :transform="`translate(${moduleWidth + 40}, 0), scale(-1, 1)`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>

    <!-- This mask and path is for a border around Title Bubble -->
    <template v-else-if="shape === 'title_bubble' && borderType">
      <mask :id="`module-shape-title-bubble-mask-${moduleId}`">
        <path
          :d="titleBubblePathOutline"
          fill="white"
        />
        <path
          :d="titleBubblePath"
          fill="black"
          :transform="backgroundMaskColor ? undefined : 'translate(20, 20)'"
        />
      </mask>
      <path
        :d="titleBubblePathOutline"
        :mask="`url(#module-shape-title-bubble-mask-${moduleId})`"
        :fill="`url(#pattern-fill-${moduleId})`"
      />
    </template>
  </svg>
</template>

<script>
import { get, isNumber } from 'lodash-es';
import { mapState } from 'pinia';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'ModuleShapeMask',
  props: {
    borderType: {
      type: String, // out of ['solid', 'clouds']
      default: null,
    },
    borderVariation: {
      type: [String, Number],
      default: '1',
    },
    moduleHeight: {
      type: Number,
      default: 0,
    },
    moduleId: {
      type: String,
      required: true,
    },
    moduleWidth: {
      type: Number,
      default: 0,
    },
    shape: {
      type: String, // out of ['circle', 'speech_bubble_lower_right', 'speech_bubble_lower_left']
      default: null,
    },
    backgroundOpacity: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      nebulaColorWhite: nebulaTokens.color.white,
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
    ]),
    backgroundMaskColor() {
      if (!isNumber(this.backgroundOpacity)) return undefined;
      const colorValue = Math.round(this.backgroundOpacity * 255);
      return `rgb(${colorValue},${colorValue},${colorValue})`;
    },
    basePentagonSvgValues() {
      // The values from this function are shared between
      // the pentagon path and its outline.

      // The path starts at the leftmost corner and goes clockwise.
      // This function takes the base path and stretches the lines
      // so that it is as large as possible within the bounds of the module.
      const svgHeight = 878;
      const svgWidth = 827;

      let verticalPadding = 0;
      let horizontalPadding = 0;
      let scaledSvgHeight = 0;
      let scaledSvgWidth = 0;

      if (this.moduleHeight / this.moduleWidth > svgHeight / svgWidth) {
        // We'll have padding on the top and bottom. Our calculations should
        // only depend on the module width
        scaledSvgHeight = this.moduleWidth * (svgHeight / svgWidth);
        scaledSvgWidth = this.moduleWidth;
        verticalPadding = (this.moduleHeight - scaledSvgHeight) / 2;
      } else {
        // We'll have padding on the sides. Our calculations should
        // only depend on the module height
        scaledSvgHeight = this.moduleHeight;
        scaledSvgWidth = this.moduleHeight * (svgWidth / svgHeight);
        horizontalPadding = (this.moduleWidth - scaledSvgWidth) / 2;
      }

      const verticalOffset = scaledSvgHeight - svgHeight;
      const horizontalOffset = scaledSvgWidth - svgWidth;

      // Delta values are the normal length of each line minus a portion
      // of the offset
      const dx1 = 221.61 + horizontalOffset * 0.3221778;
      const dy1 = -406.681 - verticalOffset * 0.5509851;

      const dx2 = 361.002 + horizontalOffset * 0.524826634;
      const dy2 = 215.492 + verticalOffset * 0.291955811;

      const dx3 = 105.238 + horizontalOffset * 0.152995566;
      const dy3 = 348.94 + verticalOffset * 0.47275565;

      const dx4 = -450.836 - horizontalOffset * 0.655427782;
      const dy4 = 173.666 + verticalOffset * 0.235288539;

      const dx5 = -237.014 - horizontalOffset * 0.344572218;
      const dy5 = -331.417 - verticalOffset * 0.4490149;

      return {
        verticalPadding,
        horizontalPadding,
        scaledSvgHeight,
        scaledSvgWidth,
        verticalOffset,
        horizontalOffset,
        dx1,
        dy1,
        dx2,
        dy2,
        dx3,
        dy3,
        dx4,
        dy4,
        dx5,
        dy5,
      };
    },
    pentagonPath() {
      const pathBeginningX = 9
        + this.basePentagonSvgValues.horizontalPadding
        + (this.backgroundMaskColor ? 20 : 0);

      const pathBeginningY = 444
        + this.basePentagonSvgValues.verticalPadding
        + this.basePentagonSvgValues.verticalOffset * 0.5509851
        + (this.backgroundMaskColor ? 20 : 0);

      return `m${pathBeginningX},${pathBeginningY}l${this.basePentagonSvgValues.dx1},${this.basePentagonSvgValues.dy1}c18.49,-33.933,61.486,-45.686,94.667,-25.879l${this.basePentagonSvgValues.dx2},${this.basePentagonSvgValues.dy2}a68.078,68.078,0,0,1,30.283,38.796l${this.basePentagonSvgValues.dx3},${this.basePentagonSvgValues.dy3}c10.286,34.106,-7.463,70.375,-40.705,83.18l${this.basePentagonSvgValues.dx4},${this.basePentagonSvgValues.dy4}c-28.956,11.154,-61.791,1.315,-79.841,-23.925l${this.basePentagonSvgValues.dx5},${this.basePentagonSvgValues.dy5}a68.074,68.074,0,0,1,-4.404,-72.172z`;
    },
    pentagonPathOutline() {
      const pathBeginningX = 11
        + this.basePentagonSvgValues.horizontalPadding;

      const pathBeginningY = 455
        + this.basePentagonSvgValues.verticalPadding
        + this.basePentagonSvgValues.verticalOffset * 0.5509851;

      return `m${pathBeginningX},${pathBeginningY}l${this.basePentagonSvgValues.dx1},${this.basePentagonSvgValues.dy1}c23.922,-43.903,79.55,-59.11,122.48,-33.483l${this.basePentagonSvgValues.dx2},${this.basePentagonSvgValues.dy2}a88.078,88.078,0,0,1,39.18,50.194l${this.basePentagonSvgValues.dx3},${this.basePentagonSvgValues.dy3}c13.308,44.126,-9.655,91.051,-52.664,107.618l${this.basePentagonSvgValues.dx4},${this.basePentagonSvgValues.dy4}c-37.463,14.431,-79.945,1.701,-103.298,-30.954l${this.basePentagonSvgValues.dx5},${this.basePentagonSvgValues.dy5}c-19.639,-27.46,-21.852,-63.731,-5.698,-93.376z`;
    },
    roundedRectanglePath() {
      // This svg path starts at the top-left corner and goes clockwise.
      // This function takes the base path and stretches the vertical and horizontal lines
      // so that it fills the module.
      const pathBeginningX = this.backgroundMaskColor ? 20 : 0;
      const pathBeginningY = this.backgroundMaskColor ? 70 : 50;
      const h1 = this.moduleWidth - 100;
      const v1 = this.moduleHeight - 100;
      const h2 = h1 * -1;
      const v2 = v1 * -1;

      return `M${pathBeginningX},${pathBeginningY}c0,-27.614,22.386,-50,50,-50h${h1}c27.614,0,50,22.386,50,50v${v1}c0,27.614,-22.386,50,-50,50h${h2}c-27.614,0,-50,-22.386,-50,-50v${v2}z`;
    },
    roundedRectanglePathOutline() {
      const h1 = this.moduleWidth - 100;
      const v1 = this.moduleHeight - 100;
      const h2 = h1 * -1;
      const v2 = v1 * -1;

      return `M0,70c0,-38.66,31.34,-70,70,-70h${h1}c38.66,0,70,31.34,70,70v${v1}c0,38.66,-31.34,70,-70,70h${h2}c-38.66,0,-70,-31.34,-70,-70v${v2}z`;
    },
    speechBubbleLowerLeftPath() {
      // This svg path starts on the right side of the "caret" and goes clockwise. This function
      // takes the base path and stretches parts of it vertically or horizontally so that it fills
      // the module. Stretching the whole path by using something like preserveAspectRatio="none"
      // disfigures the "caret" at extreme aspect ratios.
      const backgroundMaskXOffset = this.shape === 'speech_bubble_lower_right' ? -20 : 20;
      const backgroundMaskYOffset = 20;
      const verticalOffset = this.moduleHeight - 146;
      const horizontalOffset = this.moduleWidth - 565;
      const pathBeginningY = 129.279 + this.moduleHeight - 146
        + (this.backgroundMaskColor ? backgroundMaskYOffset : 0);
      const pathBeginningX = 86.7665 + (this.backgroundMaskColor ? backgroundMaskXOffset : 0);

      // v1 and v2 are vertical lines on the left and right sides of the bubble
      const v1 = -41.107 - verticalOffset;
      const v2 = 55.291 + verticalOffset;

      // h1 is an x offset for the straight line at the top of the bubble, h2 is the
      // horizontal line to the right of the caret. The horizontal line to the left
      // of the caret is left alone
      const h1 = 500 + horizontalOffset;
      const h2 = -422.916 - horizontalOffset;

      return `M${pathBeginningX},${pathBeginningY}l-13.921,13.547c-1.043,1.015,-1.564,1.522,-1.949,1.724c-1.674,0.88,-3.735,0.01,-4.272,-1.803c-0.124,-0.417,-0.124,-1.145,-0.124,-2.6c0,-3.657,0,-5.485,-0.297,-7.006c-1.25,-6.388,-6.244,-11.382,-12.631,-12.631c-1.522,-0.298,-3.35,-0.298,-7.007,-0.298h-14.566c-15.085,0,-22.628,0,-27.314,-4.686c-4.686,-4.686,-4.686,-12.229,-4.686,-27.314v${v1}c0,-14.698,0,-22.047,4.527,-26.705c4.527,-4.657,11.873,-4.865,26.565,-5.282l${h1},-14.184c15.475,-0.439,23.212,-0.659,28.059,4.053c4.849,4.713,4.849,12.453,4.849,27.934v${v2}c0,15.085,0,22.628,-4.687,27.314c-4.687,4.686,-12.229,4.686,-27.313,4.686h${h2}c-6.418,0,-9.626,0,-12.522,1.177c-2.896,1.176,-5.196,3.414,-9.795,7.89z`;
    },
    speechBubbleLowerLeftPathOutline() {
      // See notes above for an explanation; this path is the same except
      // expanded 20 pixels in each direction and rounded off. If in the future
      // the border width changes, or to create a border from a new shape, use Sketch
      // to add a stroke around the path and then convert to outlines. Figma
      // seems to have trouble converting to outlines correctly
      const verticalOffset = this.moduleHeight - 146;
      const horizontalOffset = this.moduleWidth - 565;
      const pathBeginningY = 160.212 + this.moduleHeight - 146;

      const v1 = -41.107 - verticalOffset;
      const v2 = 55.291 + verticalOffset;

      const h1 = 500 + horizontalOffset;
      const h2 = -422.916 - horizontalOffset;

      return `M52.9995,${pathBeginningY}c-11.441,0,-15.253,-0.096,-20.221,-0.764c-8.5,-1.143,-15.462,-4.007,-21.235,-9.78c-5.773,-5.773,-8.637,-12.735,-9.78,-21.235c-0.668,-4.968,-0.764,-8.78,-0.764,-20.221l0,${v1}c0,-11.161,0.093,-14.885,0.74,-19.753c1.106,-8.319,3.873,-15.158,9.446,-20.893c5.358,-5.511,11.868,-8.482,19.741,-9.884c4.761,-0.848,9.079,-1.115,18.651,-1.393c0.531,-0.016,0.958,-0.028,1.947,-0.056l${h1},-14.184c11.724,-0.333,15.626,-0.344,20.711,0.201c8.712,0.932,15.88,3.694,21.855,9.502c5.977,5.81,8.941,12.896,10.121,21.577c0.689,5.069,0.788,8.972,0.788,20.699l0,${v2}c0,11.441,-0.096,15.254,-0.764,20.222c-1.143,8.5,-4.008,15.463,-9.782,21.236c-5.773,5.771,-12.735,8.635,-21.234,9.778c-4.968,0.668,-8.78,0.764,-20.22,0.764l${h2},0c-2.493,0,-3.909,0.008,-4.861,0.034c-0.7,0.645,-1.721,1.628,-3.507,3.365l-13.922,13.548c-0.932,0.908,-1.237,1.2,-1.763,1.676c-1.47,1.327,-2.813,2.361,-4.828,3.418c-12.835,6.747,-28.636,0.078,-32.748,-13.805c-0.654,-2.2,-0.851,-3.885,-0.925,-5.867c-0.026,-0.698,-0.029,-1.118,-0.029,-2.369l-14.501,0z`;
    },
    titleBubblePath() {
      // This svg path starts at the bottom-left corner and goes clockwise.
      // This function takes the base path and stretches the vertical and horizontal lines
      // so that it fills the module.
      const verticalOffset = this.moduleHeight - 122;
      const pathBeginningX = 33 + (this.backgroundMaskColor ? 20 : 0);
      const pathBeginningY = this.moduleHeight - 1 + (this.backgroundMaskColor ? 20 : 0);

      // v1 and v2 are vertical lines on the left and right sides of the bubble
      const v1 = -46.399 - verticalOffset;
      const v2 = 56.163 + verticalOffset;

      // h1 is an x offset for the straight line at the top of the bubble.
      // The horizontal line on the bottom is handled by the final `z`
      const h1 = this.moduleWidth - 66;

      return `M${pathBeginningX},${pathBeginningY}c-15.085,0,-22.627,0,-27.314,-4.686c-4.686,-4.687,-4.686,-12.229,-4.686,-27.314v${v1}c0,-14.733,0,-22.099,4.541,-26.759c4.541,-4.66,11.905,-4.85,26.633,-5.23l${h1},-9.764c15.439,-0.399,23.159,-0.598,27.993,4.112c4.833,4.71,4.833,12.432,4.833,27.877v${v2}c0,15.085,0,22.627,-4.686,27.314c-4.687,4.686,-12.229,4.686,-27.314,4.686h-324.533z`;
    },
    titleBubblePathOutline() {
      // This svg path starts at the top-right corner and goes clockwise.
      // This function takes the base path and stretches the vertical and horizontal lines
      // so that it fills the module.
      const verticalOffset = this.moduleHeight - 170.5;
      const pathBeginningX = this.moduleWidth - 14.5;

      // v1 and v2 are vertical lines on the right and left sides of the bubble
      const v1 = 105.163 + verticalOffset;
      const v2 = -95.399 - verticalOffset;

      // h1 is an x offset for the straight line at the bottom of the bubble.
      // h2 is the x offset for the angled line on the top of the module.
      const h1 = 66 - this.moduleWidth;
      const h2 = this.moduleWidth - 65;

      return `M${pathBeginningX},0.858c11.817,-0.306,15.716,-0.307,20.792,0.25c8.691,0.951,15.84,3.722,21.8,9.529c5.957,5.806,8.912,12.88,10.088,21.545c0.687,5.06,0.786,8.955,0.786,20.655v${v1}c0,11.441,-0.096,15.253,-0.764,20.221c-1.143,8.499,-4.007,15.46,-9.781,21.237c-5.773,5.771,-12.735,8.635,-21.234,9.778c-4.968,0.668,-8.78,0.764,-20.221,0.764h${h1}c-11.441,0,-15.253,-0.096,-20.221,-0.764c-8.499,-1.143,-15.46,-4.007,-21.237,-9.781c-5.771,-5.773,-8.635,-12.735,-9.778,-21.234c-0.668,-4.968,-0.764,-8.78,-0.764,-20.221v${v2}c0,-11.187,0.093,-14.918,0.742,-19.795c1.11,-8.336,3.885,-15.185,9.475,-20.922c5.395,-5.536,11.946,-8.499,19.876,-9.88c4.78,-0.832,9.074,-1.083,18.79,-1.34l${h2},-9.806z`;
    },
    themeClass() {
      const themeName = get(this, 'currentPage.options.theme.name', 'default');
      return `border-theme--${themeName}-${this.borderVariation}`;
    },
  },
};
</script>

<style lang="stylus">
.module-shape-mask {
  fill: #414858; // default to match the Studio background
  inset-inline-start: -20px;
  position: absolute;
  top: -20px;
  z-index: 1;
}
</style>
